import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { ThumbsDown, ThumbsUp } from "lucide-react";
import { fmtDate, fmtDateTime } from "@/lib/checkins";
import { StatusCard } from "@/components/checkins/StatusCard";
import { PublicLoading, PublicMessage, PublicShell, UrgentNotice } from "@/components/checkins/public/PublicShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public/checkins/summary`;

function Rating({ token, initial }) {
  const [value, setValue] = useState(initial);
  const rate = (v) => axios.post(`${API}/${token}/rating`, { value: v }).then(() => setValue(v)).catch(() => {});
  const btn = (v, Icon, label) => (
    <button
      type="button" onClick={() => rate(v)} aria-pressed={value === v} data-testid={`summary-rate-${v}`}
      className={`inline-flex min-h-[48px] items-center gap-2 rounded-xl border-2 px-5 text-base font-semibold transition-colors duration-150 focus:outline-none focus-visible:ring-4 focus-visible:ring-sky-400 ${value === v ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-300 bg-white hover:border-zinc-500"}`}
    >
      <Icon className="h-5 w-5" aria-hidden="true" /> {label}
    </button>
  );
  return (
    <section className="rounded-2xl border border-zinc-200 bg-white p-5" data-testid="summary-rating">
      <h2 className="text-lg font-semibold text-zinc-950">Was this summary clear and useful?</h2>
      <div className="mt-3 flex flex-wrap gap-3">{btn("useful", ThumbsUp, "Yes")}{btn("not_useful", ThumbsDown, "Not really")}</div>
      {value && <p className="mt-2 text-sm text-zinc-600" role="status">Thanks for letting us know.</p>}
    </section>
  );
}

const List = ({ title, items, testId }) => (items?.length ? (
  <section data-testid={testId}>
    <h2 className="font-display text-xl font-semibold text-zinc-950">{title}</h2>
    <ul className="mt-2 space-y-2">{items.map((t) => <li key={t} className="rounded-xl bg-white p-4 text-lg leading-relaxed text-zinc-800">{t}</li>)}</ul>
  </section>
) : null);

export function SummaryView({ d, footer }) {
  const urgent = d.notices.find((n) => n.kind === "urgent");
  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">Your exercise progress review summary</p>
        <h1 className="mt-2 font-display text-3xl font-bold tracking-tight text-zinc-950 sm:text-4xl">Hi {d.first_name || "there"}</h1>
        <p className="mt-2 text-lg text-zinc-700" data-testid="summary-period">
          Week of {fmtDate(d.period?.start)} – {fmtDate(d.period?.end)} · sent {fmtDateTime(d.submitted_at)}
        </p>
        {d.typical_week !== "yes" && <span className="mt-2 inline-block rounded-full bg-zinc-200 px-3 py-1 text-sm font-semibold text-zinc-800" data-testid="summary-non-typical">Not a typical week</span>}
      </div>
      <UrgentNotice text={urgent?.text} testId="summary-urgent" />
      {d.notices.filter((n) => n.kind !== "urgent").map((n) => (
        <p key={n.kind} className="rounded-xl border-2 border-zinc-300 bg-white p-4 text-lg leading-relaxed text-zinc-800" data-testid={`summary-notice-${n.kind}`}>{n.text}</p>
      ))}
      {d.practitioner_message && (
        <section className="rounded-xl border-l-4 border-zinc-900 bg-white p-4" data-testid="summary-practitioner-message">
          <h2 className="text-sm font-semibold uppercase tracking-[0.2em] text-zinc-500">From your exercise team</h2>
          <p className="mt-1 text-lg text-zinc-800">{d.practitioner_message}</p>
        </section>
      )}
      <div className="space-y-3" data-testid="summary-cards">{d.cards.map((c) => <StatusCard key={c.code} card={c} large />)}</div>
      <List title="What is going well" items={d.going_well} testId="summary-going-well" />
      <List title="Areas to work on" items={d.priorities.map((p) => `${p.label}: ${p.next_step}`)} testId="summary-priorities" />
      <p className="text-base leading-relaxed text-zinc-600" data-testid="summary-disclaimer">{d.disclaimer}</p>
      {footer}
    </div>
  );
}

export default function PublicCheckinSummary() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    axios.get(`${API}/${token}`).then((r) => setD(r.data)).catch((e) => setErr(e?.response?.data?.detail || "This summary isn't available."));
  }, [token]);
  if (err) return <PublicShell testId="public-summary-page"><PublicMessage title="Summary unavailable" testId="summary-unavailable"><p>{err}</p></PublicMessage></PublicShell>;
  if (!d) return <PublicLoading />;
  return (
    <PublicShell clinic={d.clinic} testId="public-summary-page">
      <SummaryView d={d} footer={<Rating token={token} initial={d.rating} />} />
    </PublicShell>
  );
}
